import axios from 'axios';
import { LoginCredentials, RegisterData, AuthResponse, User } from '../types/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
      emailOrUsername: credentials.emailOrUsername,
      password: credentials.password
    });
    return response.data;
  },

  async register(userData: RegisterData): Promise<AuthResponse> {
    const response = await axios.post(`${API_BASE_URL}/api/auth/register`, userData);
    return response.data;
  },

  async logout(token: string): Promise<void> {
    await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  async getCurrentUser(): Promise<User> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No access token found');
    }

    const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  async updateProfile(profileData: Partial<User>): Promise<{ user: User }> {
    const token = localStorage.getItem('accessToken');
    const response = await axios.put(`${API_BASE_URL}/api/auth/profile`, profileData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
      refreshToken,
    });
    return response.data;
  },

  async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await axios.get(`${API_BASE_URL}/api/auth/verify-email/${token}`);
    return response.data;
  },

  async listSessions(): Promise<{ sessions: { id: string; createdAt: string; expiresAt: string; current: boolean }[] }> {
    const token = localStorage.getItem('accessToken');
    const response = await axios.get(`${API_BASE_URL}/api/auth/sessions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async revokeSession(sessionId: string): Promise<{ message: string }> {
    const token = localStorage.getItem('accessToken');
    const response = await axios.delete(`${API_BASE_URL}/api/auth/sessions/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async revokeOtherSessions(): Promise<{ message: string }> {
    const token = localStorage.getItem('accessToken');
    const response = await axios.delete(`${API_BASE_URL}/api/auth/sessions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, { email });
    return response.data;
  },

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await axios.post(`${API_BASE_URL}/api/auth/reset-password`, {
      token,
      password,
    });
    return response.data;
  },
};
